import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Facebook, CheckCircle, Loader2, HelpCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { instanciaService } from '../../services/instanciaService';
import HintTooltip from '../common/HintTooltip';
import { emitReviewEvent } from '../../utils/reviewEvents';
import { subscribeMetaOAuth } from '../../utils/metaOauthMessage';

// Extend window object to include FB SDK
declare global {
  interface Window {
    FB: any;
    fbAsyncInit: any;
  }
}

// Retorno central do OAuth da Meta para todos os subdomínios do SinTicket.
// Ver AJUSTES_META_APARECIDO.md, seção 4.
const CENTRAL_META_REDIRECT_URI = 'https://app.sinticket.com.br/meta-callback';
const SINTICKET_HOST_SUFFIX = '.sinticket.com.br';

// "state" do OAuth: origem que abriu o popup (para a página central saber a
// quem devolver o code) + nonce (garante que o code é deste login).
const buildOAuthState = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  const nonce = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return JSON.stringify({ o: window.location.origin, n: nonce });
};

interface DiscoveredPhoneNumber {
  id: string;
  display_phone_number?: string;
  verified_name?: string;
}

interface DiscoveredBusiness {
  id: string;
  name?: string | null;
}

interface DiscoveredWaba {
  id: string;
  name?: string | null;
  // Portfólio empresarial (Business) dono da WABA — lido com business_management.
  business?: DiscoveredBusiness | null;
  phoneNumbers: DiscoveredPhoneNumber[];
}

interface MetaEmbeddedSignupProps {
  // displayPhoneNumber/verifiedName servem só para nomear a instância quando o
  // usuário não digitou um nome — não vão para a API.
  onSuccess: (data: {
    accessToken: string;
    wabaId: string;
    phoneNumberId: string;
    isOnBizApp?: boolean;
    displayPhoneNumber?: string;
    verifiedName?: string;
  }) => void;
  disabled?: boolean;
}

const MetaEmbeddedSignup: React.FC<MetaEmbeddedSignupProps> = ({ onSuccess, disabled }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [configId, setConfigId] = useState('');
  const [appId, setAppId] = useState('');
  const [loadingProviderConfig, setLoadingProviderConfig] = useState(true);
  // Erro da última tentativa (mensagem real da Meta/backend), exibido na tela:
  // sem isso o usuário só via um toast genérico e perdia o motivo da falha.
  const [signupError, setSignupError] = useState<string | null>(null);

  // Preenchido após o login: o usuário sempre confirma o portfólio empresarial,
  // a WABA e o número que compartilhou (mesmo quando há só uma opção). A lista de
  // portfólios é o uso visível da permissão business_management no App Review
  // (ver AJUSTES_META_APARECIDO.md e META_TECH_PROVIDER_GUIDE.md, Seção 7.2).
  const [pendingAccessToken, setPendingAccessToken] = useState<string>('');
  const [discoveredWabas, setDiscoveredWabas] = useState<DiscoveredWaba[] | null>(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');
  const [selectedWabaId, setSelectedWabaId] = useState<string>('');
  const [selectedPhoneId, setSelectedPhoneId] = useState<string>('');
  const [pendingIsOnBizApp, setPendingIsOnBizApp] = useState(false);

  // A Meta avisa via postMessage (não pelo callback do FB.login) quando o
  // fluxo é de Coexistência: o cliente conectou um número que já usa no
  // WhatsApp Business App do celular, em vez de registrar um número novo.
  // Guardado em ref (não state) porque o callback do FB.login precisa ler o
  // valor mais recente de forma síncrona, sem depender de closures antigas.
  // Ver META_TECH_PROVIDER_GUIDE.md, Seção 6.2.
  const isOnBizAppRef = useRef(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (typeof event.origin !== 'string' || !event.origin.endsWith('facebook.com')) return;
      try {
        const data = JSON.parse(event.data);
        if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
          isOnBizAppRef.current = true;
        } else if (data.event === 'FINISH') {
          isOnBizAppRef.current = false;
        }
      } catch (_) {
        // Mensagens de outras origens/widgets não são JSON — ignorar.
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    // Check if FB SDK is already loaded
    if (window.FB) {
      setIsSdkLoaded(true);
      return;
    }

    // Wait for the async init
    const originalFbAsyncInit = window.fbAsyncInit;
    window.fbAsyncInit = function () {
      if (originalFbAsyncInit) originalFbAsyncInit();
      setIsSdkLoaded(true);
    };
  }, []);

  // App ID / Config ID configurados em SuperAdmin > Facebook/Meta (nunca mais em .env do frontend).
  useEffect(() => {
    instanciaService.obterConfigProviderMeta()
      .then(({ data }) => {
        if (data.appId) setAppId(data.appId);
        if (data.configId) setConfigId(data.configId);
      })
      .catch(() => {
        // Sem configuração no backend — usuário pode preencher manualmente abaixo.
      })
      .finally(() => setLoadingProviderConfig(false));
  }, []);
// URL de callback onde o popup devolve o "code" do OAuth (SEM barra final — a
  // Meta casa a string exata e bloqueia com barra — e SEM extensão). O proxy de
  // produção faz 301 removendo ".html" e o fallback do serve entrega o index.html
  // para o caminho: o <script> no topo de public/index.html detecta opener+?code=,
  // repassa via postMessage ANTES do bundle React rodar e fecha a janela.
  // Subdomínios *.sinticket.com.br usam o retorno CENTRAL (cadastrado uma única
  // vez no App); a página central devolve o code à origem informada no "state".
  // Fora desse domínio (ex.: localhost) usa a própria origem.
  const getRedirectUri = () =>
    window.location.hostname.endsWith(SINTICKET_HOST_SUFFIX)
      ? CENTRAL_META_REDIRECT_URI
      : `${window.location.origin}/meta-callback`;

  const resetDiscovery = () => {
    setPendingAccessToken('');
    setDiscoveredWabas(null);
    setSelectedBusinessId('');
    setSelectedWabaId('');
    setSelectedPhoneId('');
    setPendingIsOnBizApp(false);
  };

  // Processa o "code" vindo do popup: o backend troca por um access token real
  // (server-to-server, com o mesmo redirect_uri do dialog) e descobre as WABAs.
  const handleAuthCode = async (code: string, redirectUri: string) => {
    try {
      const res = await instanciaService.fetchMetaDados(code, redirectUri, appId || undefined);
      const data = res.data;
      const wabas: DiscoveredWaba[] = data.wabas || (data.wabaId ? [{ id: data.wabaId, name: null, phoneNumbers: data.phoneNumbers || [] }] : []);

      if (wabas.length === 0 || wabas.every((w) => w.phoneNumbers.length === 0)) {
        toast({ title: t('embeddedSignup.attentionTitle'), description: t('embeddedSignup.noPhoneFound'), variant: 'destructive' });
        setIsLoggingIn(false);
        return;
      }

      const firstWaba = wabas.find((w) => w.phoneNumbers.length > 0) || wabas[0];
      const firstPhone = firstWaba.phoneNumbers[0];
      // A Meta avisa via postMessage sobre Coexistência (listener acima) —
      // normalmente o evento já chegou enquanto o popup estava aberto.
      const isOnBizApp = isOnBizAppRef.current;

      // Sempre mostra a confirmação (portfólio → WABA → número), mesmo com uma
      // única opção: o usuário vê exatamente o que compartilhou com o SinTicket.
      setPendingAccessToken(data.accessToken);
      setDiscoveredWabas(wabas);
      setSelectedBusinessId(firstWaba.business?.id || '');
      setSelectedWabaId(firstWaba.id);
      setSelectedPhoneId(firstPhone?.id || '');
      setPendingIsOnBizApp(isOnBizApp);
      setSignupError(null);
      emitReviewEvent('meta-code-received');
      toast({
        title: t('embeddedSignup.loginSuccessTitle'),
        description: isOnBizApp
          ? t('embeddedSignup.coexistenceConnected')
          : t('embeddedSignup.chooseAccountAndNumber')
      });
    } catch (err: any) {
      // Mensagem real da Meta (o backend devolve em `error`) — é o que diz se
      // foi App Secret errado, redirect_uri diferente, WABA não encontrada etc.
      const detalhe = err?.response?.data?.error || err?.response?.data?.message || err?.message || null;
      setSignupError(detalhe);
      toast({
        title: t('embeddedSignup.commErrorTitle'),
        description: detalhe || t('embeddedSignup.commErrorDesc'),
        variant: 'destructive'
      });
      console.error('Erro no Embedded Signup:', err?.response?.data || err);
      // Continua na tela do Embedded Signup: o "code" expira em segundos, então
      // a saída é tentar de novo (antes caía para o formulário manual vazio, o
      // que parecia travamento).
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLaunchSignup = () => {
    if (!configId) {
      toast({ title: t('embeddedSignup.attentionTitle'), description: t('embeddedSignup.needsConfigId'), variant: 'destructive' });
      return;
    }

    const appIdParam = appId;
    if (!appIdParam) {
      toast({ title: t('embeddedSignup.attentionTitle'), description: t('embeddedSignup.missingConfigWarning'), variant: 'destructive' });
      return;
    }

    resetDiscovery();
    setSignupError(null);
    isOnBizAppRef.current = false;
    setIsLoggingIn(true);

    // Fluxo manual (dialog OAuth próprio em vez do FB.login do SDK): o SDK não
    // permite definir o redirect_uri — o popup dele usa um xd_arbiter diferente a
    // cada sessão, e a troca do code no backend falha com "Error validating
    // verification code" (OAuthException 100 / subcode 36008). Com o redirect_uri
    // explícito, o mesmo valor casa nos dois lados. Ver guia, troubleshooting.
    const redirectUri = getRedirectUri();
    const callbackOrigin = new URL(redirectUri).origin;
    const oauthState = buildOAuthState();
    const dialogUrl =
      'https://www.facebook.com/v22.0/dialog/oauth?' +
      new URLSearchParams({
        client_id: appIdParam,
        redirect_uri: redirectUri,
        state: oauthState,
        response_type: 'code',
        override_default_response_type: 'true',
        config_id: configId,
        display: 'popup',
        // Coexistência: featureType + sessionInfoVersion 3 fazem a Meta oferecer
        // "conectar o WhatsApp Business App existente" (QR code) em vez de só
        // cadastrar número novo, que recusa números já ativos no celular.
        extras: JSON.stringify({
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3'
        })
      }).toString();

    // Popup alinhado à direita da janela: deixa visível o formulário e a faixa de
    // legendas do modo de gravação (canto inferior esquerdo).
    const popupWidth = 520;
    const popupLeft = Math.max(0, window.screenX + window.outerWidth - popupWidth - 24);
    const popupTop = Math.max(0, window.screenY + 60);
    const popup = window.open(
      dialogUrl,
      'meta_embedded_signup',
      `width=${popupWidth},height=740,left=${popupLeft},top=${popupTop}`
    );

    if (!popup) {
      setIsLoggingIn(false);
      toast({ title: t('embeddedSignup.errorTitle'), description: t('embeddedSignup.sdkNotLoaded'), variant: 'destructive' });
      return;
    }
    emitReviewEvent('meta-popup-opened');

    // Captura o code devolvido pelo popup: o <script> no topo de public/index.html
    // (fallback do serve para o caminho do callback) repassa por postMessage — a
    // partir da origem do callback, que pode ser a central (outro subdomínio).
    // Quando o callback abre em aba própria sem window.opener (Chrome/Meta abrem o
    // retorno em novo contexto), a rota SPA /meta-callback (MetaCallback.tsx)
    // republica pelo BroadcastChannel/localStorage — o subscribeMetaOAuth abaixo
    // entrega o code à mesma janela. Polling de fallback cobre o caso do postMessage
    // não chegar quando o callback é da mesma origem.
    let captured = false;
    let unsubMetaOAuth: (() => void) | null = null;

    const handleOAuthMessage = (message: { type: string; code?: string; state?: string }) => {
      if (message.type === 'META_ES_CANCEL') {
        if (message.state !== oauthState) return;
        captured = true;
        cleanup();
        setIsLoggingIn(false);
        toast({ title: t('embeddedSignup.cancelledTitle'), description: t('embeddedSignup.cancelledDesc'), variant: 'destructive' });
        return;
      }
      if (message.type !== 'META_ES_CODE' || !message.code) return;
      if (message.state !== oauthState) return;
      captured = true;
      cleanup();
      setIsLoggingIn(true);
      void handleAuthCode(message.code, redirectUri);
    };

    const handleCallback = (event: MessageEvent) => {
      if (event.origin !== callbackOrigin) return;
      const data = event.data;
      if (captured || !data || typeof data.type !== 'string') return;
      handleOAuthMessage(data);
    };

    const cleanup = () => {
      window.removeEventListener('message', handleCallback);
      if (unsubMetaOAuth) unsubMetaOAuth();
      if (pollTimer !== null) clearInterval(pollTimer);
    };

    window.addEventListener('message', handleCallback);
    unsubMetaOAuth = subscribeMetaOAuth(handleOAuthMessage);

    let pollTimer: number | null = window.setInterval(() => {
      if (captured) {
        clearInterval(pollTimer as number);
        return;
      }
      try {
        if (!popup || popup.closed) {
          clearInterval(pollTimer as number);
          // O callback central fecha o popup logo após o postMessage; dá uma
          // folga para a mensagem chegar antes de considerar cancelado.
          window.setTimeout(() => {
            if (captured) return;
            window.removeEventListener('message', handleCallback);
            setIsLoggingIn(false);
            toast({ title: t('embeddedSignup.cancelledTitle'), description: t('embeddedSignup.cancelledDesc'), variant: 'destructive' });
          }, 1500);
          return;
        }
        const href = popup.location.href;
        if (href && href.indexOf('code=') !== -1) {
          const params = new URL(href).searchParams;
          const code = params.get('code');
          if (code && params.get('state') === oauthState) {
            captured = true;
            clearInterval(pollTimer as number);
            window.removeEventListener('message', handleCallback);
            void handleAuthCode(code, redirectUri);
          }
        }
      } catch (_) {
        // Popup ainda em dominio facebook.com (cross-origin) — aguarda a nossa pagina.
      }
    }, 500);
  };

  const selectedWaba = discoveredWabas?.find((w) => w.id === selectedWabaId) || null;

  // Portfólios empresariais das WABAs descobertas. O seletor de portfólio só
  // aparece quando todas as WABAs têm o portfólio identificado.
  const businessOptions: DiscoveredBusiness[] = [];
  (discoveredWabas || []).forEach((w) => {
    if (w.business && !businessOptions.some((b) => b.id === w.business!.id)) businessOptions.push(w.business);
  });
  const showBusinessSelect = !!discoveredWabas && businessOptions.length > 0 && discoveredWabas.every((w) => !!w.business);
  const wabaOptions = (discoveredWabas || []).filter((w) => !showBusinessSelect || w.business?.id === selectedBusinessId);

  const handleBusinessChange = (businessId: string) => {
    setSelectedBusinessId(businessId);
    const waba = discoveredWabas?.find((w) => w.business?.id === businessId);
    setSelectedWabaId(waba?.id || '');
    setSelectedPhoneId(waba?.phoneNumbers[0]?.id || '');
  };

  const handleWabaChange = (wabaId: string) => {
    setSelectedWabaId(wabaId);
    const waba = discoveredWabas?.find((w) => w.id === wabaId);
    setSelectedPhoneId(waba?.phoneNumbers[0]?.id || '');
  };

  const handleConfirmSelection = () => {
    if (!selectedWabaId || !selectedPhoneId) return;
    const phone = selectedWaba?.phoneNumbers.find((p) => p.id === selectedPhoneId);
    onSuccess({
      accessToken: pendingAccessToken,
      wabaId: selectedWabaId,
      phoneNumberId: selectedPhoneId,
      isOnBizApp: pendingIsOnBizApp,
      displayPhoneNumber: phone?.display_phone_number,
      verifiedName: phone?.verified_name
    });
    resetDiscovery();
  };

  if (discoveredWabas) {
    return (
      <div className="flex flex-col gap-4 border p-4 rounded-md bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="text-green-600 h-5 w-5" />
          <h3 className="font-semibold text-lg dark:text-gray-100">{t('embeddedSignup.chooseAccountTitle')}</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {t('embeddedSignup.chooseAccountDesc')}
        </p>
        {pendingIsOnBizApp && (
          <div className="text-xs bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-md px-3 py-2">
            {t('embeddedSignup.coexistenceNotice')}
          </div>
        )}

        {showBusinessSelect && (
          <div className="grid gap-1.5">
            <HintTooltip text={t('embeddedSignup.hints.business')} side="left" className="inline-flex w-fit">
              <Label className="text-xs dark:text-gray-300">{t('embeddedSignup.businessLabel')}</Label>
            </HintTooltip>
            <Select value={selectedBusinessId} onValueChange={handleBusinessChange}>
              <SelectTrigger className="dark:bg-gray-700 dark:text-white">
                <SelectValue placeholder={t('embeddedSignup.selectBusiness')} />
              </SelectTrigger>
              <SelectContent>
                {businessOptions.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name || `Business ${b.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid gap-1.5">
          <HintTooltip text={t('embeddedSignup.hints.waba')} side="left" className="inline-flex w-fit">
            <Label className="text-xs dark:text-gray-300">{t('embeddedSignup.wabaLabel')}</Label>
          </HintTooltip>
          <Select value={selectedWabaId} onValueChange={handleWabaChange}>
            <SelectTrigger className="dark:bg-gray-700 dark:text-white">
              <SelectValue placeholder={t('embeddedSignup.selectWaba')} />
            </SelectTrigger>
            <SelectContent>
              {wabaOptions.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  {w.name || `WABA ${w.id}`}
                  {!showBusinessSelect && w.business?.name ? ` — ${w.business.name}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-1.5">
          <HintTooltip text={t('embeddedSignup.hints.phone')} side="left" className="inline-flex w-fit">
            <Label className="text-xs dark:text-gray-300">{t('embeddedSignup.phoneLabel')}</Label>
          </HintTooltip>
          <Select value={selectedPhoneId} onValueChange={setSelectedPhoneId} disabled={!selectedWaba || selectedWaba.phoneNumbers.length === 0}>
            <SelectTrigger className="dark:bg-gray-700 dark:text-white">
              <SelectValue placeholder={t('embeddedSignup.selectPhone')} />
            </SelectTrigger>
            <SelectContent>
              {(selectedWaba?.phoneNumbers || []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.display_phone_number || p.id}{p.verified_name ? ` — ${p.verified_name}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <p className="text-[11px] text-muted-foreground">{t('embeddedSignup.businessSourceNote')}</p>

        <div className="flex gap-2 justify-end">
          <HintTooltip text={t('embeddedSignup.hints.cancel')}>
            <Button type="button" variant="outline" onClick={resetDiscovery}>
              {t('common.cancel')}
            </Button>
          </HintTooltip>
          <HintTooltip text={t('embeddedSignup.hints.connect')}>
            <Button type="button" onClick={handleConfirmSelection} disabled={!selectedWabaId || !selectedPhoneId}>
              {t('common.connect')}
            </Button>
          </HintTooltip>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 border p-4 rounded-md bg-gray-50 dark:bg-gray-800">
      <div className="flex items-center gap-2 mb-2">
        <Facebook className="text-blue-600" />
        <h3 className="font-semibold text-lg dark:text-gray-100">{t('embeddedSignup.title')}</h3>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-300">
        {t('embeddedSignup.description')}
      </p>

      {!loadingProviderConfig && (!configId || !appId) && (
        <div className="grid gap-3 border-l-4 border-yellow-400 pl-3 py-2">
          <p className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">{t('embeddedSignup.missingConfigWarning')}</p>
          <div>
            <div className="flex items-center gap-1">
              <Label htmlFor="appId" className="text-xs dark:text-gray-300">{t('embeddedSignup.appIdLabel')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="inline-flex items-center h-4 w-4 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 text-sm" side="top">
                  <div className="space-y-2">
                    <p className="font-medium">{t('embeddedSignup.appIdHelp.description')}</p>
                    <div className="bg-muted rounded-md p-2">
                      <p className="text-xs text-muted-foreground font-medium mb-1">{t('embeddedSignup.whereToFind')}</p>
                      <p className="text-xs">{t('embeddedSignup.appIdHelp.whereToFind')}</p>
                    </div>
                    <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 underline">
                      {t('embeddedSignup.appIdHelp.linkLabel')} ↗
                    </a>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <Input
              id="appId"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder={t('embeddedSignup.appIdPlaceholder')}
              className="mt-1 h-8 text-sm"
              disabled={disabled || isLoggingIn}
            />
          </div>
          <div>
            <div className="flex items-center gap-1">
              <Label htmlFor="configId" className="text-xs dark:text-gray-300">{t('embeddedSignup.configIdLabel')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="inline-flex items-center h-4 w-4 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 text-sm" side="top">
                  <div className="space-y-2">
                    <p className="font-medium">{t('embeddedSignup.configIdHelp.description')}</p>
                    <div className="bg-muted rounded-md p-2">
                      <p className="text-xs text-muted-foreground font-medium mb-1">{t('embeddedSignup.whereToFind')}</p>
                      <p className="text-xs">{t('embeddedSignup.configIdHelp.whereToFind')}</p>
                    </div>
                    <a href="https://developers.facebook.com/docs/whatsapp/embedded-signup" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 underline">
                      {t('embeddedSignup.configIdHelp.linkLabel')} ↗
                    </a>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <Input
              id="configId"
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
              placeholder={t('embeddedSignup.configIdPlaceholder')}
              className="mt-1 h-8 text-sm"
              disabled={disabled || isLoggingIn}
            />
          </div>
        </div>
      )}

      {signupError && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <p className="font-medium">{t('embeddedSignup.commErrorTitle')}</p>
          <p className="mt-0.5 break-words">{signupError}</p>
        </div>
      )}

      <HintTooltip text={t('embeddedSignup.hints.continueWithFacebook')} className="flex w-full">
      <Button
        type="button"
        className="w-full bg-[#1877F2] hover:bg-[#166fe5] text-white flex items-center justify-center gap-2"
        onClick={handleLaunchSignup}
        disabled={disabled || !isSdkLoaded || isLoggingIn || !configId}
      >
        {isLoggingIn ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('embeddedSignup.connecting')}
          </>
        ) : (
          <>
            <Facebook className="h-4 w-4" />
            {t('embeddedSignup.continueWithFacebook')}
          </>
        )}
      </Button>
      </HintTooltip>

      {!isSdkLoaded && (
        <p className="text-xs text-center text-gray-500">{t('embeddedSignup.loadingSdk')}</p>
      )}
    </div>
  );
};

export default MetaEmbeddedSignup;
